import { FormContainer } from "@/components/form/form-container";
import { APDFormFields } from "./APDFormFields";
import { SubmitButton } from "@/components/ui/button";
import {
  APDPayloadSchema,
  apdPayloadSchema,
  useAPD,
  useAPDEdit,
} from "@/api/trackerdata-apd";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loading } from "@/components/ui/loading";
import {
  swalErrorEditDefault,
  swalLoading,
  swalSuccessEditDefault,
} from "@/utils/lib/swal";
import { useNavigate } from "react-router-dom";
import { FormFooter } from "@/components/form/form-footer";
import { useEffect, useId } from "react";
import { Anchor } from "@/components/ui/anchor";
import { ErrorMessageFromResponse } from "@/api/types";
import {
  useMaxSuffixPermitDigit,
  useMinSuffixPermitDigit,
  useOltType,
  useITVTenant,
  useITVCompanyCode,
  useMonthTarget,
  useCheckingProfile,
} from "@/api/master-param";
import { useCurrentUser } from "../auth/useCurrentUser";
import { ROLE } from "@/utils/constant";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";

export function APDEditForm({
  id,
  isDisabled,
}: {
  id: string;
  isDisabled?: boolean;
}) {
  const { roles } = useCurrentUser();
  const disabled = hasCommonElement(roles, [
    ROLE.VENDOR_VIEW,
    ROLE.SYS_ADMIN_VIEW,
  ]);

  const { data: apd, isPending: isLoadingData } = useAPD({
    id,
  });

  const { data: minDigit } = useMinSuffixPermitDigit();
  const { data: maxDigit } = useMaxSuffixPermitDigit();

  // Load all options data
  const { data: itv_olt_type } = useOltType();
  const { data: itv_tenant } = useITVTenant();
  const { data: itv_companyCode } = useITVCompanyCode();
  const { data: itv_monthTarget } = useMonthTarget();
  const { data: profileName } = useCheckingProfile();

  const form = useForm<APDPayloadSchema>({
    resolver: zodResolver(apdPayloadSchema),
    defaultValues: {
      cityTown: "",
      hpPermit: "",
      hpDesign: "",
      suffixPermit: "",
      clusterId: "",
      projectName: "",
      fdtId: "",
      drawingIdApd: "",
      oltType: "",
      oltIdaction: "",
      combineStatus: "",
      latitude: "",
      longitude: "",
      rollOutType: "",
      tenantName: "",
      vendor: "",
      monthTarget: 0,
      yearTarget: "",
      profileName: "",
      ITV_MIN_SUFFIX_PERMIT_DIGIT: 0,
      ITV_MAX_SUFFIX_PERMIT_DIGIT: 0,
    },
  });

  useEffect(() => {
    if (!apd) {
      return;
    }

    form.reset({
      ...apd,
      yearTarget: apd.yearTarget?.toString() ?? "",
      oltType: apd.oltTypeString ?? "",
      ITV_MIN_SUFFIX_PERMIT_DIGIT: minDigit?.content?.[0]?.value
        ? Number(minDigit.content[0].value)
        : 0,
      ITV_MAX_SUFFIX_PERMIT_DIGIT: maxDigit?.content?.[0]?.value
        ? Number(maxDigit.content[0].value)
        : 0,
    });
  }, [apd, form, maxDigit, minDigit]);

  const { mutate, isPending } = useAPDEdit();
  const navigate = useNavigate();
  const formId = useId();

  function handleSubmit(values: APDPayloadSchema) {
    swalLoading.fire();

    values.monthTarget = Number(values.monthTarget);

    mutate(
      { id: id, data: values },
      {
        onSuccess: () => {
          swalSuccessEditDefault.fire().then(() => {
            navigate("/master-data/apd");
          });
        },
        onError: (err) => {
          swalErrorEditDefault
            .mixin({
              text: (err as ErrorMessageFromResponse).response?.data.data,
            })
            .fire();
        },
      },
    );
  }

  // Show loading until ALL data is available
  if (
    isLoadingData ||
    !apd ||
    !itv_olt_type ||
    !itv_tenant ||
    !itv_companyCode ||
    !itv_monthTarget ||
    !profileName
  ) {
    return <Loading />;
  }

  return (
    <>
      <FormContainer
        id={formId}
        form={form}
        onSubmit={handleSubmit}
        className="max-w-6xl columns-2 space-y-4"
      >
        <APDFormFields isDisabled={isDisabled} />
      </FormContainer>
      <FormFooter>
        <Anchor href="/master-data/apd" mood="axiata-blue-outline">
          Cancel
        </Anchor>
        {!disabled && <SubmitButton form={formId} loading={isPending} />}
      </FormFooter>
    </>
  );
}
